import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import "../../PurchasingUi.css";
import "./POApprovalDialog.css";

// A picker or confirmation may sit above the policy editor. Only the top dialog
// handles focus and Escape; closing it returns focus to the underlying editor.
const openDialogs = [];
let previousBodyOverflow = "";
const focusableSelector = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]';

function isVisibleControl(element) {
  if (element.tabIndex < 0 || element.type === "hidden") return false;
  for (let parent = element; parent; parent = parent.parentElement) {
    if (parent.hidden || parent.getAttribute("aria-hidden") === "true") return false;
    const style = window.getComputedStyle(parent);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (parent.tagName === "DETAILS" && !parent.open) {
      const summary = [...parent.children].find((child) => child.tagName === "SUMMARY");
      if (!summary?.contains(element)) return false;
    }
  }
  return true;
}

export default function POApprovalDialog({
  isOpen, onClose, title, subtitle, children, footer, headerActions, scrollableHeading = false, width = "medium", busy = false, className = "",
}) {
  const { themeId, languageId } = usePmsTheme();
  const titleId = useId();
  const subtitleId = useId();
  const cardRef = useRef(null);
  const overlayRef = useRef(null);
  const callbacks = useRef({ onClose, busy });
  callbacks.current = { onClose, busy };

  useEffect(() => {
    if (!isOpen) return undefined;
    const card = cardRef.current;
    const previousFocus = document.activeElement;
    if (!openDialogs.length) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openDialogs.push(card);
    overlayRef.current.style.zIndex = 100020 + openDialogs.length;
    const isTop = () => openDialogs[openDialogs.length - 1] === card;
    const focusable = () => [...card.querySelectorAll(focusableSelector)].filter(isVisibleControl);
    const focusFirst = () => (focusable()[0] || card).focus();
    focusFirst();
    const keepFocus = (event) => {
      if (isTop() && !card.contains(event.target)) focusFirst();
    };
    const onKey = (event) => {
      if (!isTop() || event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!callbacks.current.busy) callbacks.current.onClose();
      }
      if (event.key !== "Tab") return;
      const elements = focusable();
      const first = elements[0] || card;
      const last = elements[elements.length - 1] || card;
      if (!card.contains(document.activeElement) || document.activeElement === card) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("focusin", keepFocus);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("focusin", keepFocus);
      document.removeEventListener("keydown", onKey);
      const wasTop = isTop();
      const index = openDialogs.indexOf(card);
      if (index !== -1) openDialogs.splice(index, 1);
      if (!openDialogs.length) document.body.style.overflow = previousBodyOverflow;
      if (wasTop && previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return createPortal(
    <div className="pilcore-purchasing-overlay po-dialog" data-theme={themeId} ref={overlayRef}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy && openDialogs[openDialogs.length - 1] === cardRef.current) onClose();
      }}>
      <section className={`po-dialog__card po-dialog__card--${width} ${className}`} ref={cardRef}
        role="dialog" aria-modal="true" aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined} aria-busy={busy} tabIndex={-1}>
        <header className="po-dialog__header">
          <div {...(scrollableHeading ? { className: "po-dialog__scrollableHeading", role: "group", tabIndex: 0, "aria-labelledby": titleId } : {})}>
            <h2 id={titleId}>{title}</h2>{subtitle && <p id={subtitleId}>{subtitle}</p>}
          </div>
          <div className="po-dialog__headerActions">{headerActions}<button className="po-dialog__close" type="button" onClick={onClose} disabled={busy}
            aria-label={languageId === "es" ? "Cerrar" : languageId === "ca" ? "Tanca" : "Close"}><FiX aria-hidden="true" /></button></div>
        </header>
        <div className="po-dialog__body">{children}</div>
        {footer && <footer className="po-dialog__footer">{footer}</footer>}
      </section>
    </div>, document.body
  );
}
